import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiCheck, FiX, FiUpload, FiTrash2, FiExternalLink,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_BADGE = {
  DRAFT:     { background: '#fef3c7', color: '#d97706' },
  CONFIRMED: { background: '#dcfce7', color: '#16a34a' },
  CANCELLED: { background: '#fee2e2', color: '#dc2626' },
};

const METHOD_LABEL = {
  cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other',
};

const DOC_LABEL = { receipt: 'Receipt', bill: 'Bill', invoice: 'Invoice', other: 'Other' };

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ minWidth: 170, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 14, color: '#111', flex: 1 }}>{value ?? '—'}</span>
    </div>
  );
}

export default function AdminExpenseDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();

  const [expense,   setExpense]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [acting,    setActing]    = useState(false);

  const fetchExpense = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`/expenses/${id}`);
      setExpense(data.expense);
    } catch {
      toast.error('Failed to load expense');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchExpense(); }, [fetchExpense]);

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this expense? Financial details will be locked.')) return;
    try {
      setActing(true);
      await API.post(`/expenses/${id}/confirm`);
      toast.success('Expense confirmed');
      fetchExpense();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to confirm');
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this expense? This cannot be undone.')) return;
    try {
      setActing(true);
      await API.post(`/expenses/${id}/cancel`);
      toast.success('Expense cancelled');
      fetchExpense();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setActing(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const docType = window.prompt('Document type (receipt / bill / invoice / other):', 'receipt') || 'other';
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      const { data: upload } = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await API.post(`/expenses/${id}/documents`, {
        document_type: docType,
        document_name: file.name,
        file_path:     upload.url,
      });
      toast.success('Document uploaded');
      fetchExpense();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await API.delete(`/expenses/${id}/documents/${docId}`);
      toast.success('Document removed');
      fetchExpense();
    } catch {
      toast.error('Failed to remove document');
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  if (!expense) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Expense not found.</div>;

  const isConfirmed = expense.status === 'CONFIRMED';
  const isCancelled = expense.status === 'CANCELLED';
  const isDraft     = expense.status === 'DRAFT';
  const cardStyle   = { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 20 };

  return (
    <div style={{ padding: 24, maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/admin/expenses')}
            style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <FiArrowLeft size={15} />
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>{expense.expense_number}</h2>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{fmt(expense.expense_date)}</div>
          </div>
          <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 20, fontWeight: 600, ...STATUS_BADGE[expense.status] }}>
            {expense.status.charAt(0) + expense.status.slice(1).toLowerCase()}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {isDraft && (
            <>
              <button
                onClick={() => navigate(`/admin/expenses/${id}/edit`)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                <FiEdit2 size={13} /> Edit
              </button>
              <button
                onClick={handleConfirm}
                disabled={acting}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: acting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: acting ? 0.7 : 1 }}
              >
                <FiCheck size={13} /> Confirm
              </button>
              <button
                onClick={handleCancel}
                disabled={acting}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: acting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: acting ? 0.7 : 1 }}
              >
                <FiX size={13} /> Cancel
              </button>
            </>
          )}
          {isConfirmed && (
            <button
              onClick={handleCancel}
              disabled={acting}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: acting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: acting ? 0.7 : 1 }}
            >
              <FiX size={13} /> Cancel Expense
            </button>
          )}
        </div>
      </div>

      {isCancelled && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 18, fontSize: 13, color: '#dc2626' }}>
          This expense has been cancelled and is excluded from active expense totals.
        </div>
      )}

      {/* Expense Info */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Expense Information</h3>
        <InfoRow label="Category"       value={expense.category?.name} />
        <InfoRow label="Description"    value={expense.description} />
        <InfoRow label="Amount"         value={money(expense.grand_total)} />
        <InfoRow label="Payment Method" value={METHOD_LABEL[expense.payment_method] || expense.payment_method} />
        <InfoRow label="Paid From Account" value={expense.bankAccount?.account_name || '—'} />
      </div>

      

      {/* Documents */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Documents ({expense.documents?.length || 0})</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', background: 'var(--primary)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <FiUpload size={13} />
            {uploading ? 'Uploading…' : 'Upload'}
            <input type="file" style={{ display: 'none' }} onChange={handleDocUpload} accept=".pdf,.jpg,.jpeg,.png" />
          </label>
        </div>
        {(!expense.documents || expense.documents.length === 0) ? (
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>No documents attached.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {expense.documents.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{doc.document_name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{DOC_LABEL[doc.document_type] || doc.document_type}</div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <a href={doc.file_path} target="_blank" rel="noopener noreferrer" style={{ color: '#6b7280', display: 'flex', alignItems: 'center' }} title="View">
                    <FiExternalLink size={15} />
                  </a>
                  <button onClick={() => handleDocDelete(doc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }} title="Remove">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit */}
      {/* <div style={{ ...cardStyle, marginBottom: 0 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Audit</h3>
        <InfoRow label="Created"  value={expense.created_at ? new Date(expense.created_at).toLocaleString('en-IN') : '—'} />
        <InfoRow label="Updated"  value={expense.updated_at ? new Date(expense.updated_at).toLocaleString('en-IN') : '—'} />
      </div> */}
    </div>
  );
}